/*
 * Pure helpers for the Shots tab's zone-analysis views.
 *
 * Kept free of React/canvas imports so they unit-test in the node env. The
 * data comes from shots_tracking.shot_distribution_by_player_zone, keyed by the
 * player's OWN origin zone ("back_left", …); each entry's `all_shots` holds
 * where those shots landed on the opponent half (horizontal / vertical splits).
 */
import { largestRemainderPercents } from '@/lib/utils';
import type { ShotPlayerZone } from '@/types/statistics';
import { backendKeysForChip } from './shotTypes';

export type ZoneDistribution =
  | Record<string, ShotPlayerZone>
  | null
  | undefined;

/**
 * Shots the player hit FROM this origin zone, filtered to a shot-type chip.
 * 'all' is the zone's precomputed total (serves included, as upstream counts
 * them); a specific chip sums the matching `by_shot_type` entries (overhead
 * folds smash). Zones the player never hit that type from return 0.
 */
export function zoneShotCount(zone: ShotPlayerZone, type: string): number {
  if (type === 'all') return zone.shot_count;
  return backendKeysForChip(type).reduce(
    (n, k) => n + (zone.by_shot_type[k]?.shot_count ?? 0),
    0,
  );
}

/** The origin zone the player hit the most shots of `type` from (null if none).
    With no shots of that type anywhere, falls back to the overall busiest zone
    so the grid still centres somewhere meaningful (its destination reads 0%). */
export function busiestPlayerZone(
  dist: ZoneDistribution,
  type: string = 'all',
): string | null {
  if (!dist) return null;
  let best: string | null = null;
  let max = -1;
  for (const [key, zone] of Object.entries(dist)) {
    const c = zoneShotCount(zone, type);
    if (c > max) {
      max = c;
      best = key;
    }
  }
  if (type !== 'all' && max <= 0) return busiestPlayerZone(dist, 'all');
  return best;
}

/**
 * The zone to actually display: the user's selection when it still exists in
 * this player's data, otherwise the busiest zone for the active shot type. This
 * keeps a sensible default on open, self-heals when the selected player changes
 * (their zones differ), and re-centres on the busiest zone for a new filter.
 */
export function resolveShotZone(
  dist: ZoneDistribution,
  selected: string | null,
  type: string = 'all',
): string | null {
  if (dist && selected && dist[selected]) return selected;
  return busiestPlayerZone(dist, type);
}

export type DestinationAxis = 'lateral' | 'depth';
export interface DestinationSlice {
  key: string;
  pct: number;
}

// Canonical band order, near→far of the camera's reading direction.
const LATERAL_ORDER = ['left', 'middle', 'right'] as const;
const DEPTH_ORDER = ['short', 'medium', 'long'] as const;

// The normalized landing split for one shot-type chip from this origin zone.
// 'all' uses the zone's precomputed `all_shots` aggregate; a specific chip
// blends its `by_shot_type` entries, weighting each (already-normalized) split
// by that type's shot count, then renormalizes. Empty when the type was never
// hit from here. `field` picks the lateral (horizontal) or depth (vertical) map.
function typeLandingSplit(
  zone: ShotPlayerZone,
  field: 'horizontal_positions' | 'vertical_positions',
  type: string,
): Record<string, number> {
  if (type === 'all') return zone.all_shots[field];
  const acc: Record<string, number> = {};
  let total = 0;
  for (const k of backendKeysForChip(type)) {
    const bt = zone.by_shot_type[k];
    if (!bt || bt.shot_count <= 0) continue;
    total += bt.shot_count;
    for (const [band, frac] of Object.entries(bt[field])) {
      acc[band] = (acc[band] ?? 0) + frac * bt.shot_count;
    }
  }
  if (total > 0) for (const band of Object.keys(acc)) acc[band] /= total;
  return acc;
}

/**
 * Where the selected zone's shots landed, as integer percents that always sum
 * to 100 (largest-remainder), in canonical band order. `lateral` reads the
 * horizontal split (left/middle/right), `depth` the vertical (short/med/long).
 * `type` scopes the split to one shot-type chip ('all' = every shot).
 */
export function destinationPercents(
  zone: ShotPlayerZone,
  axis: DestinationAxis,
  type: string = 'all',
): DestinationSlice[] {
  const order = axis === 'lateral' ? LATERAL_ORDER : DEPTH_ORDER;
  const field =
    axis === 'lateral' ? 'horizontal_positions' : 'vertical_positions';
  const src = typeLandingSplit(zone, field, type);
  const pcts = largestRemainderPercents(order.map((k) => src[k] ?? 0));
  return order.map((key, i) => ({ key, pct: pcts[i] }));
}
